import { useEffect, useState } from 'react'

import { droppedVoiceNamesWarning } from './droppedVoiceNamesWarning'

import styles from './RoomDialog.module.scss'

import type { GtRoom, GtSpeaker } from '@/api/internal'
import Button from '@/components/Button'
import DialogWrapper from '@/components/Dialog'
import {
  normalizeSpeakerName,
  planRoomJoin,
  retainedVoiceName,
  sameRoomKey,
  SPEAKER_NAME_MAX_CODEPOINTS,
} from '@/pages/app/AttributionViewer/annotate/gtModel'

export type RoomDialogMode =
  /** Put these roster speakers (acoustic voices) into one labelled room. */
  { type: 'group'; speakers: GtSpeaker[] } | { type: 'rename'; room: GtRoom }

export interface RoomDialogProps {
  mode: RoomDialogMode | null
  rooms: readonly GtRoom[]
  /** The roster; a person whose name exports like the room label is flagged, since finalize would refuse it. */
  speakers: readonly GtSpeaker[]
  close: () => void
  onSubmit: (label: string, voiceNames?: Record<string, string | null>) => void
}

const initialLabel = (mode: RoomDialogMode | null, rooms: readonly GtRoom[]): string => {
  if (mode?.type === 'rename') return mode.room.label
  const roomId = mode?.speakers[0]?.room_id
  return roomId && mode?.speakers.every((speaker) => speaker.room_id === roomId)
    ? (rooms.find((room) => room.id === roomId)?.label ?? '')
    : ''
}

export const RoomDialog = ({ mode, rooms, speakers, close, onSubmit }: RoomDialogProps) => {
  const isOpen = mode !== null
  const fromLabel = initialLabel(mode, rooms)
  const [label, setLabel] = useState(fromLabel)
  const [selectedRoomId, setSelectedRoomId] = useState('')
  const [voiceNames, setVoiceNames] = useState<Record<string, string>>({})

  useEffect(() => {
    if (isOpen) {
      setLabel(fromLabel)
      setSelectedRoomId(
        mode?.type === 'group' ? (rooms.find((room) => sameRoomKey(room.label, fromLabel))?.id ?? '') : ''
      )
      setVoiceNames(
        mode?.type === 'group'
          ? Object.fromEntries(mode.speakers.map((speaker) => [speaker.id, retainedVoiceName(speaker) ?? '']))
          : {}
      )
    }
  }, [isOpen, fromLabel, mode?.type, rooms])

  const selectedRoom = rooms.find((room) => room.id === selectedRoomId)
  const trimmed = (selectedRoom?.label ?? label).trim()
  const excludedRoomId = mode?.type === 'rename' ? mode.room.id : null
  const collision =
    trimmed === ''
      ? null
      : (rooms.find((room) => room.id !== excludedRoomId && sameRoomKey(room.label, trimmed)) ?? null)
  const voices = mode?.type === 'group' ? mode.speakers : []
  const voiceIds = new Set(voices.map((voice) => voice.id))
  const joinOnly = mode?.type === 'group' && voices.length < 2
  const names = voices.map((voice) => normalizeSpeakerName(voiceNames[voice.id] ?? '')).filter(Boolean)
  const duplicateVoiceName = new Set(names).size !== names.length
  const namingNewRoom = mode?.type === 'group' && !joinOnly && !selectedRoom && !collision
  const droppedNames =
    mode?.type === 'group' && collision ? planRoomJoin(speakers, voiceIds, collision.id).droppedNames : []
  const personCollision =
    trimmed === '' || collision
      ? null
      : (speakers.find((sp) => sp.room_id === null && !voiceIds.has(sp.id) && sameRoomKey(sp.name, trimmed)) ?? null)
  const title =
    mode?.type === 'rename' ? `Rename room "${mode.room.label}"` : joinOnly ? 'Add to a room' : 'Group as a room'
  const submitLabel =
    mode?.type === 'rename' ? (collision ? 'Merge rooms' : 'Rename') : joinOnly || collision ? 'Join room' : 'Group'
  const alreadyInRoom = mode?.type === 'group' && collision && voices.every((voice) => voice.room_id === collision.id)

  return (
    <DialogWrapper isOpen={isOpen} title={title} close={close}>
      <div className={styles.body}>
        {mode?.type === 'group' && (
          <>
            <p className={styles.explain}>
              A room is a shared device (a conference-room mic, a meeting-room tile), not a person. Each voice keeps its
              own identity and turns; they are scored as voices of one room.
            </p>
            {!namingNewRoom && (
              <ul className={styles.voices} aria-label="Voices to group">
                {voices.map((speaker) => (
                  <li key={speaker.id} className={styles.voice}>
                    {speaker.voice_name ?? speaker.name}
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
        {mode?.type === 'group' && rooms.length > 0 && (
          <label className={styles.label} htmlFor="gt-existing-room">
            Existing room
            <select
              id="gt-existing-room"
              className={styles.input}
              value={selectedRoomId}
              onChange={(event) => {
                setSelectedRoomId(event.target.value)
                setLabel('')
              }}
            >
              <option value="">{joinOnly ? 'Select a room' : 'Create a new room'}</option>
              {rooms.map((room) => (
                <option key={room.id} value={room.id}>
                  {room.label}
                </option>
              ))}
            </select>
          </label>
        )}
        {!selectedRoom && !joinOnly && (
          <label className={styles.label} htmlFor="gt-room-label-input">
            Room label
            <input
              id="gt-room-label-input"
              className={styles.input}
              value={label}
              placeholder="e.g. Great Hall"
              maxLength={SPEAKER_NAME_MAX_CODEPOINTS}
              onChange={(event) => setLabel(event.target.value)}
            />
          </label>
        )}
        {namingNewRoom && (
          <ul className={styles.voiceNames} aria-label="Names for room voices">
            {voices.map((speaker) => (
              <li key={speaker.id}>
                <label className={styles.label}>
                  Name for {speaker.name}
                  <input
                    className={styles.input}
                    value={voiceNames[speaker.id] ?? ''}
                    placeholder="Optional person name"
                    maxLength={SPEAKER_NAME_MAX_CODEPOINTS}
                    onChange={(event) => setVoiceNames((names) => ({ ...names, [speaker.id]: event.target.value }))}
                  />
                </label>
              </li>
            ))}
          </ul>
        )}
        {collision && mode?.type === 'rename' && (
          <p className={styles.warning}>
            {`A room labelled "${collision.label}" already exists. Renaming moves every voice of "${fromLabel}" into it.`}
          </p>
        )}
        {alreadyInRoom && <p className={styles.explain}>These voices are already in this room.</p>}
        {droppedNames.length > 0 && <p className={styles.warning}>{droppedVoiceNamesWarning(droppedNames)}</p>}
        {namingNewRoom && duplicateVoiceName && (
          <p className={styles.warning}>Give each voice in the room a different name.</p>
        )}
        {personCollision && (
          <p className={styles.warning}>
            {`A speaker named "${personCollision.name}" exports under the same key as this room; finalize will refuse the collision until one of them is renamed.`}
          </p>
        )}
        <div className={styles.actions}>
          <Button title="Cancel" type="link" onClick={close} />
          <Button
            title={submitLabel}
            type="dark"
            isDisabled={
              !isOpen ||
              !trimmed ||
              alreadyInRoom ||
              (mode.type === 'rename' && trimmed === fromLabel) ||
              (namingNewRoom && duplicateVoiceName)
            }
            onClick={() => {
              const voiceNameUpdates: Record<string, string | null> = {}
              if (namingNewRoom) {
                for (const voice of voices) {
                  const typed = voiceNames[voice.id]?.trim() || null
                  if (typed) voiceNameUpdates[voice.id] = typed
                  // An empty field on a named speaker is a clear, not "keep the roster name".
                  else if (retainedVoiceName(voice)) voiceNameUpdates[voice.id] = null
                }
              }
              onSubmit(trimmed, namingNewRoom ? voiceNameUpdates : undefined)
              close()
            }}
          />
        </div>
      </div>
    </DialogWrapper>
  )
}
